import { SCORE, taskDef } from './tasks.js';
import type { Chapter, Seat, SeatStats, ShowState, TaskState } from './types.js';

/**
 * The leaderboard: every game scored the same way against its own par (tasks.ts), the table
 * ranked by it at the end of every era, and each guest's evening in numbers at the end.
 *
 * Everything here is public. Points and counts are on the TV and every phone; nothing in them
 * says who is a saboteur, who volunteered, or how anyone voted.
 */

/** The eras that have a game to score. */
export const SCORED_CHAPTERS = [1, 2, 3, 4, 5] as const;
export type ScoredChapter = (typeof SCORED_CHAPTERS)[number];

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** Points for one finished game. The same sum for every game; see SCORE. */
export function taskPoints(parMs: number, ms: number, mistakes: number): number {
  const counted = ms + mistakes * SCORE.MISTAKE_MS;
  const raw = Math.round((SCORE.PAR_POINTS * parMs) / Math.max(1, counted));
  return clamp(raw, SCORE.MIN_POINTS, SCORE.MAX_POINTS);
}

export interface ScoredRun {
  /** The time the room accepted, or null when the phone did not say. */
  ms: number | null;
  mistakes: number | null;
  points: number;
}

/**
 * What the room makes of a phone's WELL DONE. The time and the mistakes come from the phone, so
 * they are held to what a real game can be: a second and a half at the least, ten minutes at
 * the most, and 0–50 mistakes. A phone from before scoring says neither, and scores par.
 */
export function scoreRun(parMs: number, ms: unknown, mistakes: unknown): ScoredRun {
  const time = typeof ms === 'number' && Number.isFinite(ms) ? clamp(Math.round(ms), SCORE.MIN_MS, SCORE.MAX_MS) : null;
  const wrong =
    typeof mistakes === 'number' && Number.isFinite(mistakes)
      ? clamp(Math.round(mistakes), 0, SCORE.MAX_MISTAKES)
      : null;
  return { ms: time, mistakes: wrong, points: taskPoints(parMs, time ?? parMs, wrong ?? 0) };
}

/** A seat's points for one era, or for the whole voyage so far. */
export function pointsFor(state: Pick<ShowState, 'scores'>, seat: number, chapter: Chapter | 'all'): number {
  const key = String(seat);
  // A phone or TV newer than the room it talks to gets no scores at all, and must not fall over.
  const scores = state.scores ?? {};
  if (chapter !== 'all') return scores[String(chapter)]?.[key] ?? 0;
  return SCORED_CHAPTERS.reduce((sum, ch) => sum + (scores[String(ch)]?.[key] ?? 0), 0);
}

export interface LeaderRow {
  seat: number;
  callsign: string;
  /** The post they chose, shown under the call sign (Marek, 2026-09-30). */
  profession: NonNullable<Seat['profession']>;
  points: number;
  /** 1 for the most points. Equal points share a rank, and the next rank skips: 1, 2, 2, 4. */
  rank: number;
}

/** Just what a leaderboard reads, so the TV and the phones can pass their own copy of the room. */
export interface BoardSource {
  seats: readonly Pick<Seat, 'seat' | 'callsign' | 'profession'>[];
  scores: ShowState['scores'];
}

/**
 * Everyone aboard, most points first, by call sign. A guest who has not finished a game this
 * era is still on it, with nothing, so the whole table is always there. Equal points are listed
 * in seat order.
 */
export function leaderboard(state: BoardSource, chapter: Chapter | 'all'): LeaderRow[] {
  const rows = state.seats
    .filter(
      (s): s is typeof s & { callsign: string; profession: NonNullable<Seat['profession']> } =>
        s.callsign !== null && s.profession !== null,
    )
    .map((s) => ({ seat: s.seat, callsign: s.callsign, profession: s.profession, points: pointsFor(state, s.seat, chapter) }))
    .sort((a, b) => b.points - a.points || a.seat - b.seat);
  const ranked: LeaderRow[] = [];
  for (const [i, row] of rows.entries()) {
    const above = ranked[i - 1];
    ranked.push({ ...row, rank: above && above.points === row.points ? above.rank : i + 1 });
  }
  return ranked;
}

export function emptyStats(): SeatStats {
  return { fixes: 0, meetings: 0, ballots: 0 };
}

// ---------------------------------------------------------------------------
// Bookkeeping for the reducer. Pure, and keyed by seat like everything else in the room.

/**
 * Add to a seat's points for an era: its phone game (once, when it is done) and, since 2026-09-27,
 * its moves in the table game (Marek: "contribute to the total amount of points").
 */
export function withPoints(state: ShowState, chapter: Chapter, seat: number, points: number): ShowState {
  const era = String(chapter);
  const had = state.scores[era]?.[String(seat)] ?? 0;
  return { ...state, scores: { ...state.scores, [era]: { ...(state.scores[era] ?? {}), [String(seat)]: had + points } } };
}

/** One more of something for each of these seats. */
export function countFor(state: ShowState, seats: readonly number[], field: keyof SeatStats): ShowState {
  if (seats.length === 0) return state;
  const stats = { ...state.stats };
  for (const seat of seats) {
    const was = stats[String(seat)] ?? emptyStats();
    stats[String(seat)] = { ...was, [field]: was[field] + 1 };
  }
  return { ...state, stats };
}

function reseat<T>(record: Record<string, T>, move: (seat: number) => number): Record<string, T> {
  return Object.fromEntries(Object.entries(record).map(([seat, value]) => [String(move(Number(seat))), value]));
}

/** Two guests changed places: their points and counts go with them. */
export function moveSeatRecords(state: ShowState, move: (seat: number) => number): ShowState {
  return {
    ...state,
    scores: Object.fromEntries(Object.entries(state.scores).map(([era, seats]) => [era, reseat(seats, move)])),
    stats: reseat(state.stats, move),
    signals: reseat(state.signals ?? {}, move),
  };
}

/** A guest checked out: whoever takes the seat next starts from nothing. */
export function dropSeatRecords(state: ShowState, seat: number): ShowState {
  const key = String(seat);
  const without = <T>(record: Record<string, T>) => Object.fromEntries(Object.entries(record).filter(([s]) => s !== key));
  return {
    ...state,
    scores: Object.fromEntries(Object.entries(state.scores).map(([era, seats]) => [era, without(seats)])),
    stats: without(state.stats),
    signals: without(state.signals ?? {}),
  };
}

// ---------------------------------------------------------------------------
// The evening, for one guest's own phone at the end.

export interface EveningSummary {
  seat: number;
  callsign: string | null;
  /** Points over the whole voyage, and where that puts them among everyone aboard. */
  total: number;
  rank: number | null;
  players: number;
  /** Games finished tonight. */
  games: number;
  /** The era they scored most in, with their rank in it. Null before any points. */
  bestEra: { chapter: ScoredChapter; points: number; rank: number } | null;
  /** The game finished quickest against its own par, on time alone. Null without a time. */
  fastest: { chapter: ScoredChapter; label: string; ms: number; parMs: number } | null;
  fixes: number;
  meetings: number;
  ballots: number;
}

export type SummarySource = BoardSource & Pick<ShowState, 'stats' | 'tasks'> & Partial<Pick<ShowState, 'gameSet'>>;

/** One guest's evening: their numbers only, read off the public room. */
export function eveningFor(state: SummarySource, seat: number): EveningSummary {
  const board = leaderboard(state, 'all');
  const mine = board.find((row) => row.seat === seat);
  let bestEra: EveningSummary['bestEra'] = null;
  for (const chapter of SCORED_CHAPTERS) {
    const points = pointsFor(state, seat, chapter);
    if (points > 0 && (!bestEra || points > bestEra.points)) {
      const rank = leaderboard(state, chapter).find((row) => row.seat === seat)?.rank ?? 1;
      bestEra = { chapter, points, rank };
    }
  }
  const finished = Object.values(state.tasks).filter((t): t is TaskState => t.seat === seat && t.done);
  let fastest: EveningSummary['fastest'] = null;
  for (const task of finished) {
    const def = taskDef(task.chapter, task.index, state.gameSet);
    if (!def || task.ms === undefined || task.chapter === 0) continue;
    const pace = def.parMs / task.ms;
    if (!fastest || pace > fastest.parMs / fastest.ms) {
      fastest = { chapter: task.chapter, label: def.label, ms: task.ms, parMs: def.parMs };
    }
  }
  const stats = (state.stats ?? {})[String(seat)] ?? emptyStats();
  return {
    seat,
    callsign: state.seats.find((s) => s.seat === seat)?.callsign ?? null,
    total: mine?.points ?? pointsFor(state, seat, 'all'),
    rank: mine?.rank ?? null,
    players: board.length,
    games: finished.length,
    bestEra,
    fastest,
    ...stats,
  };
}
